import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  fetchSafetyAudits,
  fetchSafetyAuditRepeatables,
  fetchSafetyAuditScanOnDemands,
  reassignSafetyAudits,
  sendAuditReminder
} from '../../api/v4';
import history from '../../history';
import { getActiveLocationId, getActiveProject } from '../../selectors/company';
import { getAccessLevel } from '../../selectors/users';
import HeaderAndFooter from '../../components/HeaderAndFooter';
import Header from '../../components/Header';
import List from '../../components/List';
import ReassignUserModal from '../../components/Modal/reassignUserModal';
import RepeatableList from '../../components/RepeatableList';
import TypeSelection from '../../components/TypeSelection';
import ImmediateList from '../../components/ImmediateList';

import styles from './safetyAudits.modules.scss';

export default function Audits() {
  const location = useLocation().pathname;
  const auditType = location.split('/').pop();
  const auditName =
    auditType === 'loto'
      ? 'Lockout/Tagout Procedure'
      : `${auditType.charAt(0).toUpperCase() + auditType.slice(1)} Audit`;
  const activeGroup = useSelector(getActiveLocationId);
  const activeProject = useSelector(getActiveProject);
  const accessLevel = useSelector(getAccessLevel);
  const [audits, setAudits] = useState([]);
  const [repeatables, setRepeatables] = useState([]);
  const [scans, setScans] = useState([]);
  const [schedule, setSchedule] = useState('Active');
  const [selectedTab, setSelectedTab] = useState(0);
  const [ids, setIds] = useState([]);
  const [openReassignModal, setOpenReassignModal] = useState(false);
  const [reassignedUser, setReassignedUser] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    fetchSafetyAudits(auditType).then(setAudits);
    if (accessLevel > 100) {
      fetchSafetyAuditRepeatables(auditType).then(setRepeatables);
      fetchSafetyAuditScanOnDemands(auditType).then(setScans);
    }
  }, [accessLevel, auditType]);

  const reassignAudits = () => {
    reassignSafetyAudits({ ids, reassignedUser }).then(() => {
      setOpenReassignModal(false);
      setReassignedUser(null);
      fetchSafetyAudits(auditType).then(setAudits);
    });
  };

  const getGroupIds = ids => {
    const selectedTasks = audits.filter(t => ids.includes(t._id));

    return selectedTasks.map(t => t.groupId);
  };

  const handleRowClick = ids => {
    if (!ids?.length) return;
    setIds(ids);

    setGroups(getGroupIds(ids));
    setOpenReassignModal(true);
  };

  const header = (
    <Header
      title={auditName + 's'}
      rightButtons={{
        color: 'blue',
        text: `Add ${auditName}`,
        onClick: () => history.push(`/app/audits/${auditType}/create/`)
      }}
    />
  );

  const repeatingColumns = [
    {
      key: 'title',
      label: `${auditName}(s)`
    },
    {
      key: 'assignedTo',
      datatype: 'users'
    },
    {
      key: 'lastUpdatedAt',
      label: 'Last Updated',
      datatype: 'date'
    },
    {
      key: 'repeatingOn',
      label: 'Repeat On',
      datatype: 'dow',
      frequency: 'frequency'
    }
  ];

  const onDemandColumns = [
    {
      key: 'title',
      label: `${auditName}(s)`
    },
    {
      key: 'lastUpdatedAt',
      label: 'Last Updated',
      datatype: 'date'
    },
    {
      key: 'qrCodeUrl',
      label: 'QR Code',
      datatype: 'url',
      text: 'Download QR Code'
    }
  ];

  const activeColumns = [
    {
      key: 'safetyAuditNumber',
      label: `${auditType === 'loto' ? 'Procedure' : 'Audit'} Number`
    },
    {
      key: 'title',
      label: `${auditName}(s)`
    },
    {
      key: 'assignedTo',
      datatype: 'users'
    },
    {
      key: 'timeSpent',
      label: 'Time Spent'
    },
    {
      key: 'dueDate',
      label: 'Due By',
      datatype: 'futureDate'
    },
    {
      key: 'lastUpdatedAt',
      label: 'Last Updated',
      datatype: 'date'
    }
  ];

  if (!activeProject) {
    activeColumns.unshift({
      key: 'projectId',
      label: 'Area',
      datatype: 'project'
    });
    repeatingColumns.unshift({
      key: 'projectId',
      label: 'Area',
      datatype: 'project'
    });
    onDemandColumns.unshift({
      key: 'projectId',
      label: 'Area',
      datatype: 'project'
    });
  }

  if (!activeGroup) {
    activeColumns.unshift({
      key: 'groupId',
      label: 'Group/Est.',
      datatype: 'group'
    });
    repeatingColumns.unshift({
      key: 'groupId',
      label: 'Group/Est.',
      datatype: 'group'
    });
    onDemandColumns.unshift({
      key: 'groupId',
      label: 'Group/Est.',
      datatype: 'group'
    });
  }

  const statuses = ['New', 'In Progress', 'Severity Review', 'Closed'];

  const reportContent = statuses.map((status, index) => {
    return audits.filter(jsa => jsa.currentWfStage === index);
  });

  const tabs = {
    selected: selectedTab,
    onClick: i => setSelectedTab(i),
    list: statuses.map((name, i) => ({
      color: 'blue',
      name,
      count: reportContent[i]?.length
    }))
  };

  return (
    <HeaderAndFooter Header={header}>
      {accessLevel > 100 ? (
        <TypeSelection
          selected={schedule}
          selectedArray={['Active', 'Repeating', 'On Demand']}
          onClick={value => setSchedule(value)}
        />
      ) : null}
      {schedule === 'Repeating' ? (
        <RepeatableList
          repeatables={repeatables}
          repeatingColumns={repeatingColumns}
          handleRowClick={row =>
            history.push(
              `/app/audits/${auditType}/create/${row._id}/repeatable`
            )
          }
          saveKey="repeatingRas"
          type={`${auditName}s`}
          updateData={() =>
            fetchSafetyAuditRepeatables(auditType).then(response =>
              setRepeatables(response)
            )
          }
        />
      ) : schedule === 'On Demand' ? (
        <List
          saveKey="scanRas"
          rowClick={row =>
            history.push(`/app/audits/${auditType}/create/${row._id}/scan`)
          }
          dataIsHash
          data={scans}
          settings={onDemandColumns}
        />
      ) : (
        <ImmediateList
          actions={
            accessLevel >= 500
              ? [
                  {
                    color: 'blue',
                    label: `Reassign ${
                      auditType === 'loto' ? 'Procedure' : 'Audit'
                    }`,
                    onClick: handleRowClick
                  },
                  {
                    color: 'blue',
                    label: 'Send Reminder',
                    onClick: sendAuditReminder
                  }
                ]
              : null
          }
          saveKey="immediateRas"
          rowClick={row =>
            row.isCompleted
              ? history.push(`/app/audits/${auditType}/summary/${row._id}`)
              : history.push(`/app/audits/${auditType}/perform/${row._id}`)
          }
          data={reportContent[selectedTab]}
          dataIsHash
          settings={activeColumns}
          tabs={tabs}
          getRowId={r => r._id}
          hideCheckboxFor="isCompleted"
        />
      )}
      <ReassignUserModal
        title={`Reassign ${auditName}s`}
        reassignUser={reassignedUser}
        disableSubmit={!reassignedUser}
        message={
          <div className={styles.reassignText}>
            {`All of the selected ${auditName}s will be reassigned to the user
            below.`}
          </div>
        }
        onRequestClose={() => {
          setOpenReassignModal(false);
          setReassignedUser(null);
        }}
        isOpen={openReassignModal}
        employeeFieldLabel="New Assignee"
        onChange={setReassignedUser}
        submitActions={reassignAudits}
        groups={groups}
        permissions={[100, 400, 500, 900]}
      />
    </HeaderAndFooter>
  );
}
